import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, Settings, X } from "lucide-react";
import {
  acceptAll,
  rejectAll,
  setConsent,
  getConsent,
  hasResponded,
} from "../lib/cookieConsent";

const PRIMARY = "#00658D";
const ACCENT = "#00AEEF";

export default function CookieBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    // Delay slightly so the banner doesn't compete with initial page LCP.
    const timer = setTimeout(() => {
      if (!hasResponded()) {
        setOpen(true);
      } else {
        const existing = getConsent();
        if (existing) {
          setPrefs({ analytics: !!existing.analytics, marketing: !!existing.marketing });
        }
      }
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setOpen(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setOpen(false);
    setShowSettings(false);
  };

  const handleSave = () => {
    setConsent(prefs);
    setOpen(false);
    setShowSettings(false);
  };

  if (!open) return null;

  return (
    <>
      {/* Cookie banner — bottom card, fixed */}
      {!showSettings && (
        <div
          className="fixed left-4 right-4 bottom-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[100] rounded-2xl shadow-2xl border bg-white animate-in slide-in-from-bottom-6 fade-in duration-300"
          style={{ borderColor: "rgba(189,200,209,0.4)" }}
          role="dialog"
          aria-live="polite"
          aria-label={t("cookies.title")}
          data-testid="cookie-banner"
        >
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-3 mb-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(180deg, ${ACCENT} 0%, ${PRIMARY} 100%)` }}
              >
                <Cookie className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-base mb-1" style={{ color: "#0c2d4a" }}>
                  {t("cookies.title")}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "#3e4850" }}>
                  {t("cookies.message")}{" "}
                  <Link
                    to="/privacy"
                    className="font-medium underline hover:opacity-80"
                    style={{ color: PRIMARY }}
                    data-testid="cookie-privacy-link"
                    onClick={() => setOpen(false)}
                  >
                    {t("cookies.privacyLink")}
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-white text-sm transition-all hover:opacity-95 active:scale-[0.98]"
                style={{
                  background: `linear-gradient(180deg, ${ACCENT} 0%, ${PRIMARY} 100%)`,
                  boxShadow: "0 4px 12px rgba(0,101,141,0.25)",
                }}
                data-testid="cookie-accept-all"
              >
                {t("cookies.acceptAll")}
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-slate-50 active:scale-[0.98]"
                style={{
                  background: "#fff",
                  color: PRIMARY,
                  border: `1px solid rgba(0,101,141,0.25)`,
                }}
                data-testid="cookie-reject-all"
              >
                {t("cookies.rejectAll")}
              </button>
            </div>

            <button
              onClick={() => setShowSettings(true)}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
              style={{ color: "#6e7881" }}
              data-testid="cookie-customize"
            >
              <Settings className="w-3.5 h-3.5" />
              {t("cookies.customize")}
            </button>
          </div>
        </div>
      )}

      {/* Settings modal — fullscreen overlay */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-label={t("cookies.customize")}
          data-testid="cookie-settings-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200"
            style={{ borderColor: "rgba(189,200,209,0.4)" }}
          >
            <div className="p-6 border-b" style={{ borderColor: "rgba(189,200,209,0.3)" }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold mb-1" style={{ color: "#0c2d4a" }}>
                    {t("cookies.title")}
                  </h2>
                  <p className="text-sm" style={{ color: "#6e7881" }}>
                    {t("cookies.message")}{" "}
                    <Link
                      to="/privacy"
                      className="font-medium underline hover:opacity-80"
                      style={{ color: PRIMARY }}
                      data-testid="cookie-settings-privacy-link"
                      onClick={() => {
                        setShowSettings(false);
                        setOpen(false);
                      }}
                    >
                      {t("cookies.privacyLink")}
                    </Link>
                  </p>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  aria-label="Close"
                  data-testid="cookie-settings-close"
                >
                  <X className="w-5 h-5" style={{ color: "#6e7881" }} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Necessary — always on */}
              <CategoryRow
                title={t("cookies.categories.necessary.title")}
                desc={t("cookies.categories.necessary.desc")}
                checked={true}
                disabled={true}
                badge={t("cookies.categories.necessary.alwaysOn")}
                testId="cookie-cat-necessary"
              />
              <CategoryRow
                title={t("cookies.categories.analytics.title")}
                desc={t("cookies.categories.analytics.desc")}
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="cookie-cat-analytics"
              />
              <CategoryRow
                title={t("cookies.categories.marketing.title")}
                desc={t("cookies.categories.marketing.desc")}
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="cookie-cat-marketing"
              />
            </div>

            <div className="p-6 border-t flex flex-col sm:flex-row gap-2" style={{ borderColor: "rgba(189,200,209,0.3)" }}>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-white text-sm transition-all hover:opacity-95 active:scale-[0.98]"
                style={{
                  background: `linear-gradient(180deg, ${ACCENT} 0%, ${PRIMARY} 100%)`,
                  boxShadow: "0 4px 12px rgba(0,101,141,0.25)",
                }}
                data-testid="cookie-save-prefs"
              >
                {t("cookies.save")}
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-slate-50 active:scale-[0.98]"
                style={{ background: "#fff", color: PRIMARY, border: `1px solid rgba(0,101,141,0.25)` }}
                data-testid="cookie-settings-accept-all"
              >
                {t("cookies.acceptAll")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const CategoryRow = ({ title, desc, checked, disabled, onChange, badge, testId }) => (
  <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: "#f7f9fe" }}>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-semibold text-sm" style={{ color: "#0c2d4a" }}>
          {title}
        </h3>
        {badge && (
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: "rgba(0,101,141,0.1)", color: PRIMARY }}
          >
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs leading-relaxed" style={{ color: "#3e4850" }}>
        {desc}
      </p>
    </div>
    {/* Toggle */}
    <button
      onClick={() => !disabled && onChange && onChange(!checked)}
      disabled={disabled}
      className={`relative w-10 h-6 rounded-full transition-all flex-shrink-0 ${
        disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
      }`}
      style={{
        background: checked ? PRIMARY : "#cbd5e1",
      }}
      role="switch"
      aria-checked={checked}
      data-testid={testId}
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? "translateX(16px)" : "translateX(0)" }}
      />
    </button>
  </div>
);
